import { refreshAccessToken } from './authRefresh';

const DEFAULT_SKIP_URLS = ['/auth/login'];

/**
 * Attaches the shared 401 -> refresh -> retry handling to an axios instance.
 *
 * Replaces the three near-identical copies that used to live in api.js,
 * classicApi.js and examAPI.js, each refreshing independently.
 */
export const attachAuthRefreshInterceptor = (
  instance,
  skipUrls = DEFAULT_SKIP_URLS,
) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      // error.config is set for every axios error; error.response.config is not,
      // so reading url off the latter threw a TypeError on timeouts, cancelled
      // requests and CORS failures instead of rejecting cleanly.
      const originalConfig = error?.config || error?.response?.config;

      // No response at all: network error, timeout, cancellation, CORS.
      // Nothing a token refresh can fix.
      if (!error?.response) {
        return Promise.reject(error);
      }

      if (
        error.response.status !== 401 ||
        !originalConfig ||
        originalConfig._retriedAfterRefresh ||
        skipUrls.includes(originalConfig.url)
      ) {
        return Promise.reject(error);
      }

      // Guard against a retry loop if the refreshed token is rejected too.
      originalConfig._retriedAfterRefresh = true;

      try {
        await refreshAccessToken();
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }

      // Re-running the instance re-runs the request interceptor, which stamps
      // the freshly stored access token onto the retried request.
      return instance(originalConfig);
    },
  );
};

export default attachAuthRefreshInterceptor;
